import { Fragment, useState, type FormEvent } from "react";
import { EVENT_TYPES, type WebhookDeliveryView, type WebhookView } from "../../shared/apiV1";
import { useI18n } from "../i18n/I18nProvider";
import { ApiError } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { marketApi, type WebhooksView } from "./api";
import { IntCard, SecretReveal, useWhen } from "./IntegrationsParts";

/**
 * Integraciones › Webhooks: the shop's endpoints. A new one (or a rotated secret) shows its signing
 * secret once; each shows whether it's working, can be tested, switched off and on, and opens its
 * latest deliveries with Reintentar.
 */
export function WebhooksSection() {
  const { t } = useI18n();
  const data = useAsync((signal) => marketApi.webhooks(signal), []);
  const [view, setView] = useState<WebhooksView | null>(null);
  const [secret, setSecret] = useState<{ title: string; text: string; note: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [all, setAll] = useState(true);
  const current = view ?? data.data;
  if (!current || current.max === 0) return null;
  const full = current.webhooks.length >= current.max;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === "webhook_limit"
          ? t("wh.limit", { max: current.max })
          : err instanceof ApiError && (err.code === "invalid_url" || err.code === "validation_error")
            ? t("wh.badUrl")
            : t("error.generic"),
      );
    } finally {
      setBusy(false);
    }
  };

  const create = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const types = all ? ["*"] : EVENT_TYPES.filter((ev) => f.get(`ev-${ev}`) === "on");
    void run(async () => {
      const r = await marketApi.createWebhook({ url: String(f.get("url") ?? "").trim(), types, description: String(f.get("description") ?? "").trim() || undefined });
      setView({ webhooks: r.webhooks, max: r.max });
      setSecret({ title: t("wh.createdTitle"), text: r.secret, note: t("wh.created") });
      setAdding(false);
      setAll(true);
    });
  };

  return (
    <IntCard
      id="wh-title"
      title={t("wh.title")}
      meta={t("wh.count", { count: current.webhooks.length, max: current.max })}
      action={
        !adding &&
        !full && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => (setAdding(true), setSecret(null))}>
            {t("wh.new")}
          </button>
        )
      }
    >
      <p className="muted small">{t("wh.hint")}</p>
      {secret && <SecretReveal title={secret.title} note={secret.note} secret={secret.text} />}
      {adding && (
        <form className="int-form stack-sm" onSubmit={create}>
          <div className="field">
            <label htmlFor="wh-url">{t("wh.url")}</label>
            <input id="wh-url" name="url" type="url" required maxLength={500} pattern="https://.+" placeholder="https://" autoFocus />
          </div>
          <div className="field">
            <label htmlFor="wh-desc">{t("wh.description")}</label>
            <input id="wh-desc" name="description" maxLength={100} />
          </div>
          <fieldset className="check-list">
            <legend>{t("wh.events")}</legend>
            <label>
              <input type="radio" name="wh-mode" checked={all} onChange={() => setAll(true)} /> {t("wh.all")}
            </label>
            <label>
              <input type="radio" name="wh-mode" checked={!all} onChange={() => setAll(false)} /> {t("wh.pick")}
            </label>
            {!all && <p className="muted small">{t("wh.listingHint")}</p>}
            {!all && (
              <div className="int-events">
                {EVENT_TYPES.map((ev) => (
                  <label key={ev}>
                    <input type="checkbox" name={`ev-${ev}`} /> {t(`wh.ev.${ev}` as never)}
                  </label>
                ))}
              </div>
            )}
          </fieldset>
          <div className="row-actions">
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
              {t("wh.create")}
            </button>
            <button type="button" className="btn btn-link btn-sm" onClick={() => setAdding(false)}>
              {t("api.cancel")}
            </button>
          </div>
        </form>
      )}
      {current.webhooks.length === 0 ? (
        !adding && <p className="int-empty">{t("wh.empty")}</p>
      ) : (
        <table className="stats-table int-table">
          <thead>
            <tr>
              <th scope="col">{t("wh.colUrl")}</th>
              <th scope="col">{t("wh.events")}</th>
              <th scope="col">{t("wh.colStatus")}</th>
              <th scope="col">
                <span className="sr-only">{t("api.colActions")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {current.webhooks.map((w) => (
              <WebhookRow key={w.id} webhook={w} busy={busy} onView={setView} onSecret={(text) => setSecret({ title: t("wh.rotatedTitle"), text, note: t("wh.rotated") })} run={run} />
            ))}
          </tbody>
        </table>
      )}
      {full && <p className="muted small">{t("wh.limit", { max: current.max })}</p>}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </IntCard>
  );
}

function WebhookRow({
  webhook: w,
  busy,
  onView,
  onSecret,
  run,
}: {
  webhook: WebhookView;
  busy: boolean;
  onView: (v: WebhooksView) => void;
  onSecret: (secret: string) => void;
  run: (fn: () => Promise<void>) => Promise<void>;
}) {
  const { t } = useI18n();
  const when = useWhen();
  const [test, setTest] = useState<{ ok: boolean; text: string } | null>(null);
  const [open, setOpen] = useState(false);
  const healthy = w.active && !w.failingSince;
  const status = w.active ? (w.failingSince ? t("wh.failingPill") : t("wh.active")) : t("wh.off");
  const note = w.active ? (w.failingSince ? t("wh.failing", { date: when(w.failingSince) }) : null) : w.disabledReason === "failing" ? t("wh.offFailing") : null;
  return (
    <Fragment>
      <tr>
        <td data-label={t("wh.colUrl")}>
          <strong className="api-url">{w.url}</strong>
          {w.description && <span className="muted small int-sub">{w.description}</span>}
        </td>
        <td data-label={t("wh.events")} className="int-nowrap">{w.types.includes("*") ? t("wh.allEvents") : t("wh.someEvents", { count: w.types.length })}</td>
        <td data-label={t("wh.colStatus")}>
          <span className={healthy ? "pill pill-paid" : "pill pill-warn"}>{status}</span>
          {note && <span className="muted small int-sub">{note}</span>}
        </td>
        <td className="int-actions">
          <div className="row-actions">
            <button
              type="button"
              className="btn btn-link btn-sm"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  const r = await marketApi.testWebhook(w.id);
                  setTest(r.ok ? { ok: true, text: t("wh.testOk", { code: r.code ?? 0, ms: r.ms ?? 0 }) } : { ok: false, text: t("wh.testFail", { error: r.error ?? "" }) });
                })
              }
            >
              {t("wh.test")}
            </button>
            <button type="button" className="btn btn-link btn-sm" aria-expanded={open} onClick={() => setOpen(!open)}>
              {t("wh.deliveries")}
            </button>
            <button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => void run(async () => onView(await marketApi.updateWebhook(w.id, { active: !w.active })))}>
              {w.active ? t("wh.turnOff") : t("wh.turnOn")}
            </button>
            <button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => void run(async () => onSecret((await marketApi.rotateWebhookSecret(w.id)).secret))}>
              {t("wh.rotate")}
            </button>
            <button
              type="button"
              className="btn btn-link btn-sm int-danger"
              disabled={busy}
              onClick={() => {
                if (window.confirm(t("wh.deleteConfirm", { url: w.url }))) void run(async () => onView(await marketApi.deleteWebhook(w.id)));
              }}
            >
              {t("wh.delete")}
            </button>
          </div>
        </td>
      </tr>
      {(test || open) && (
        <tr className="int-detail">
          <td colSpan={4}>
            {test && (
              <p className="small" role="status">
                <span className={test.ok ? "pill pill-paid" : "pill pill-warn"}>{t("wh.testLabel")}</span> {test.text}
              </p>
            )}
            {open && <Deliveries id={w.id} />}
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function Deliveries({ id }: { id: number }) {
  const { t } = useI18n();
  const when = useWhen();
  const data = useAsync((signal) => marketApi.webhookDeliveries(id, signal), [id]);
  const [retried, setRetried] = useState<number[]>([]);
  if (!data.data) return null;
  if (!data.data.items.length) return <p className="muted small">{t("wh.noDeliveries")}</p>;
  return (
    <table className="stats-table int-table int-deliveries">
      <thead>
        <tr>
          <th scope="col">{t("api.colDate")}</th>
          <th scope="col">{t("wh.colEvent")}</th>
          <th scope="col">{t("wh.colResult")}</th>
          <th scope="col">{t("wh.colResponse")}</th>
          <th scope="col">
            <span className="sr-only">{t("api.colActions")}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {data.data.items.map((d: WebhookDeliveryView) => (
          <tr key={d.id}>
            <td data-label={t("api.colDate")} className="int-nowrap">
              {when(d.deliveredAt ?? d.createdAt)}
            </td>
            <td data-label={t("wh.colEvent")}>{t(`wh.ev.${d.type}` as never)}</td>
            <td data-label={t("wh.colResult")}>
              <span className={d.status === "ok" ? "pill pill-paid" : d.status === "skipped" ? "pill" : "pill pill-warn"}>{t(`wh.status.${d.status}` as never)}</span>
            </td>
            <td data-label={t("wh.colResponse")}>
              {d.responseCode ?? "—"}
              {d.error && <span className="muted small int-sub">{d.error}</span>}
            </td>
            <td className="int-actions">
              {(d.status === "dead" || d.status === "pending") &&
                (retried.includes(d.id) ? (
                  <span className="muted small">{t("wh.retried")}</span>
                ) : (
                  <button type="button" className="btn btn-link btn-sm" onClick={() => void marketApi.retryWebhookDelivery(id, d.id).then(() => setRetried([...retried, d.id]))}>
                    {t("wh.retry")}
                  </button>
                ))}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
